import type { StrictOmit } from '@frontend/duck-tape';
import { Clickable, Icon, Text } from '@frontend/web-react';
import { mergeAndConcatClasses, safeCss } from '@frontend/web-utils';
import type { CheckboxProps as MCheckboxProps } from '@mantine/core';
import { Checkbox as MCheckbox } from '@mantine/core';

export type CheckboxProps = StrictOmit<MCheckboxProps, 'classNames' | 'color' | 'onChange' | 'variant'> & {
  onChange: (checked: boolean) => void;
};

const classNames: MCheckboxProps['classNames'] = {
  body: safeCss('text-p-sm text-textSecondary'),
  input: safeCss('border-2 border-textSecondary rounded-sm'),
  root: safeCss('flex-1'),
};

// For now only supports one size, can add more variants if needed
/**
 * Should only be used if rendering a "one off" checkbox for presentational purposes. If you need to render multiple checkboxes, use CheckboxGroupInput.
 */
export const Checkbox = ({ checked, className, label, onChange, ...props }: CheckboxProps) => {
  return (
    <Clickable
      className={mergeAndConcatClasses(
        'flex cursor-pointer justify-center flex-row hover-background-effect',
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
    >
      <MCheckbox
        checked={checked}
        classNames={classNames}
        color={'slate.6'}
        // TODO (ethan): Currently we don't handle indeterminate steps
        icon={({ className }) => <Icon className={className} color="inverse" name="IconCheck" size="xs" stroke={4} />}
        label={
          label ? (
            <Text color="secondary" isSelectable={false} onClick={(e) => e.stopPropagation()} type="p-sm">
              {label}
            </Text>
          ) : null
        }
        onChange={(e) => onChange(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        {...props}
      />
    </Clickable>
  );
};
